'use client';
import React, { useState, useMemo } from 'react';
import { Calendar, Download, Filter, Search, ChevronDown, ChevronUp, TrendingUp, DollarSign, Users, Award, GitBranch } from 'lucide-react';
import { DISTRIBUTORS, COMMISSION_HISTORY, getRankDotColor, Rank } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';
import CommissionComponentChart from './CommissionComponentChart';
import GenerationBreakdownTable from './GenerationBreakdownTable';

type SortKey = 'name' | 'rank' | 'psv' | 'gsv' | 'retail' | 'l1' | 'eb' | 'eeb' | 'dblg1' | 'breakaway' | 'total';

const PERIODS = ['Jan 2026', 'Feb 2026', 'Mar 2026', 'Apr 2026'];

// Build per-distributor commission rows
function buildCommissionRows() {
  return Object.values(DISTRIBUTORS).map(d => {
    const isExec = ['Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(d.rank);
    const ebRate = isExec ? (d.gsv >= 50000 ? 0.15 : d.gsv >= 25000 ? 0.14 : d.gsv >= 15000 ? 0.13 : d.gsv >= 10000 ? 0.12 : d.gsv >= 5000 ? 0.11 : d.gsv >= 3000 ? 0.10 : d.gsv >= 2000 ? 0.09 : 0) : 0;
    const retail = d.psv * 0.25;
    const l1 = d.children.reduce((s, id) => {
      const c = DISTRIBUTORS[id];
      return s + (c ? c.psv * 0.05 : 0);
    }, 0);
    const eb = isExec ? d.gsv * ebRate : 0;
    const eeb = isExec && d.gsv >= 3000 ? d.gsv * 0.05 : 0;
    const dblg1 = isExec && d.gsv >= 3000 && d.g1BreakawayCount > 0 ? d.gsv * 0.10 : 0;
    const breakaway = isExec && d.g1BreakawayCount >= 1 ? d.gsv * 0.025 : 0;
    const volumePath = eb + eeb + dblg1;
    const depthPath = eb + breakaway;
    const wealthBonus = Math.max(volumePath, depthPath);
    const total = retail + l1 + (isExec ? wealthBonus : 0);
    const wealthPath = volumePath >= depthPath ? 'Volume' : 'Depth';
    return { ...d, retail, l1, eb, eeb, dblg1, breakaway, total, ebRate, wealthPath, isExec };
  });
}

export default function CommissionReportsClient() {
  const [selectedPeriod, setSelectedPeriod] = useState('Apr 2026');
  const [search, setSearch] = useState('');
  const [rankFilter, setRankFilter] = useState('All');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showRankDrop, setShowRankDrop] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 8;

  const rows = useMemo(() => buildCommissionRows(), []);

  const filtered = useMemo(() => {
    return rows
      .filter(r => {
        const ms = search === '' || r.name.toLowerCase().includes(search.toLowerCase()) || r.id.includes(search);
        const mr = rankFilter === 'All' || r.rank === rankFilter;
        const ma = activeFilter === 'all' || (activeFilter === 'active' ? r.active : !r.active);
        return ms && mr && ma;
      })
      .sort((a, b) => {
        const av = a[sortKey as keyof typeof a] as number | string;
        const bv = b[sortKey as keyof typeof b] as number | string;
        if (typeof av === 'string' && typeof bv === 'string') {
          return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
      });
  }, [rows, search, rankFilter, activeFilter, sortKey, sortDir]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const totalPayout = filtered.reduce((s, r) => s + r.total, 0);
  const totalEB = filtered.reduce((s, r) => s + r.eb, 0);
  const totalBreakaway = filtered.reduce((s, r) => s + r.breakaway + r.dblg1, 0);
  const totalRetail = filtered.reduce((s, r) => s + r.retail, 0);
  const activeCount = filtered.filter(r => r.active).length;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const RANKS: Rank[] = ['Distributor', 'Brand Partner Gold', 'Brand Partner Lapis', 'Brand Partner Ruby', 'Executive', 'Provisional Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'];

  const colHeaders: { key: SortKey; label: string }[] = [
    { key: 'name', label: 'Name' },
    { key: 'rank', label: 'Rank' },
    { key: 'psv', label: 'PSV' },
    { key: 'gsv', label: 'GSV' },
    { key: 'retail', label: 'Retail' },
    { key: 'l1', label: 'L1 Bonus' },
    { key: 'eb', label: 'Exec Bonus' },
    { key: 'eeb', label: 'EEB' },
    { key: 'dblg1', label: 'DBLG1' },
    { key: 'breakaway', label: 'Breakaway' },
    { key: 'total', label: 'Total' },
  ];

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-zinc-800 bg-zinc-900/40">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-100">Commission Reports</h1>
            <p className="text-xs text-zinc-500 mt-0.5">Period-based payout summary across all compensation components</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5">
              <Calendar size={14} className="text-zinc-500" />
              <select
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value)}
                className="bg-transparent text-sm text-zinc-300 outline-none cursor-pointer"
              >
                {PERIODS.map(p => <option key={`period-${p}`} value={p}>{p}</option>)}
              </select>
            </div>
            <button className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-colors active:scale-95">
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-screen-2xl mx-auto px-6 py-5 space-y-5">
          {/* Summary KPIs */}
          <div className="grid grid-cols-5 gap-3">
            {[
              { label: 'Total Payout', value: `$${totalPayout.toFixed(0)}`, sub: `${filtered.length} distributors`, icon: DollarSign, color: 'text-violet-400', bg: 'bg-violet-500/10' },
              { label: 'Exec Bonus Total', value: `$${totalEB.toFixed(0)}`, sub: 'GSV-tiered 9–15%', icon: Award, color: 'text-amber-400', bg: 'bg-amber-500/10' },
              { label: 'Breakaway + DBLG1', value: `$${totalBreakaway.toFixed(0)}`, sub: 'G1–G6 generation pool', icon: GitBranch, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { label: 'Retail Profit Total', value: `$${totalRetail.toFixed(0)}`, sub: 'GRP across network', icon: TrendingUp, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { label: 'Active Distributors', value: `${activeCount}`, sub: `of ${filtered.length} total`, icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
            ].map(kpi => (
              <div key={`rkpi-${kpi.label}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${kpi.bg}`}>
                  <kpi.icon size={17} className={kpi.color} />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600">{kpi.label}</p>
                  <p className="text-xl font-bold font-mono tabular-nums text-zinc-100">{kpi.value}</p>
                  <p className="text-[10px] text-zinc-600">{kpi.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-5">
            <CommissionComponentChart data={COMMISSION_HISTORY} />
            <GenerationBreakdownTable />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 w-56">
              <Search size={14} className="text-zinc-500" />
              <input
                type="text"
                placeholder="Search distributor..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="bg-transparent text-sm text-zinc-200 placeholder-zinc-600 outline-none w-full"
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setShowRankDrop(!showRankDrop)}
                className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-300 hover:border-zinc-600 transition-colors"
              >
                <Filter size={13} className="text-zinc-500" />
                <span>{rankFilter === 'All' ? 'All Ranks' : rankFilter}</span>
                <ChevronDown size={13} className="text-zinc-500" />
              </button>
              {showRankDrop && (
                <div className="absolute top-full mt-1 left-0 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 w-52 animate-fade-in">
                  {['All', ...RANKS].map(r => (
                    <button
                      key={`rf-${r}`}
                      onClick={() => { setRankFilter(r); setShowRankDrop(false); setPage(1); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 transition-colors ${rankFilter === r ? 'text-violet-300' : 'text-zinc-300'}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 bg-zinc-800/40 border border-zinc-700 rounded-lg p-0.5">
              {(['all', 'active', 'inactive'] as const).map(f => (
                <button
                  key={`af-${f}`}
                  onClick={() => { setActiveFilter(f); setPage(1); }}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${activeFilter === f ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <span className="ml-auto text-xs text-zinc-600 font-mono">{filtered.length} results</span>
          </div>

          {/* Commission Table */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {colHeaders.map(col => (
                      <th
                        key={`rth-${col.key}`}
                        onClick={() => handleSort(col.key)}
                        className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-3 cursor-pointer hover:text-zinc-400 whitespace-nowrap transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key && (
                            sortDir === 'desc' ? <ChevronDown size={10} className="text-violet-400" /> : <ChevronUp size={10} className="text-violet-400" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-zinc-600 px-4 py-3 whitespace-nowrap">Wealth Path</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(row => (
                    <tr key={`rrow-${row.id}`} className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getRankDotColor(row.rank)}`} />
                          <div>
                            <p className="text-sm font-semibold text-zinc-200 whitespace-nowrap">{row.name}</p>
                            <p className="text-[10px] text-zinc-600 font-mono">{row.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><RankBadge rank={row.rank} size="xs" /></td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-mono font-semibold ${row.psv >= 100 ? 'text-zinc-300' : 'text-red-400'}`}>{row.psv}</span>
                      </td>
                      <td className="px-4 py-3"><span className="text-sm font-mono text-zinc-300">{(row.gsv / 1000).toFixed(1)}K</span></td>
                      <td className="px-4 py-3"><span className="text-sm font-mono text-emerald-400">${row.retail.toFixed(0)}</span></td>
                      <td className="px-4 py-3"><span className="text-sm font-mono text-blue-400">${row.l1.toFixed(0)}</span></td>
                      <td className="px-4 py-3"><span className={`text-sm font-mono ${row.eb > 0 ? 'text-purple-400' : 'text-zinc-700'}`}>${row.eb.toFixed(0)}</span></td>
                      <td className="px-4 py-3"><span className={`text-sm font-mono ${row.eeb > 0 ? 'text-violet-400' : 'text-zinc-700'}`}>${row.eeb.toFixed(0)}</span></td>
                      <td className="px-4 py-3"><span className={`text-sm font-mono ${row.dblg1 > 0 ? 'text-amber-400' : 'text-zinc-700'}`}>${row.dblg1.toFixed(0)}</span></td>
                      <td className="px-4 py-3"><span className={`text-sm font-mono ${row.breakaway > 0 ? 'text-red-400' : 'text-zinc-700'}`}>${row.breakaway.toFixed(0)}</span></td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold font-mono text-zinc-100">${row.total.toFixed(0)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {row.isExec ? (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${row.wealthPath === 'Volume' ? 'bg-amber-500/15 border-amber-500/30 text-amber-400' : 'bg-blue-500/15 border-blue-500/30 text-blue-400'}`}>
                            {row.wealthPath}
                          </span>
                        ) : (
                          <span className="text-[10px] text-zinc-700">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-10 text-center text-sm text-zinc-600">
                        No distributors match the current filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-600">
                Showing {Math.min((page - 1) * perPage + 1, filtered.length)}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={`pg-${p}`}
                    onClick={() => setPage(p)}
                    className={`w-7 h-7 rounded text-xs font-semibold transition-all ${page === p ? 'bg-violet-600 text-white' : 'text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}